import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { get_especialidades } from "../services/api";
import { FormattedAsignatura } from "../interfaces/ObjetivosAprendizaje/iAsignaturas";
import { useAuthContext } from "../hooks/Auth";
import SpinnerUmmIA from "../components/Loading/SpinnerUmmIA";

interface Props {
  selectedEspecialidadId: string;
  onEspecialidadChange: (especialidad: FormattedAsignatura) => void;
  disabled?: boolean;
}

const GetEspecialidad: FC<Props> = ({
  selectedEspecialidadId,
  onEspecialidadChange,
  disabled = false,
}) => {
  const { user } = useAuthContext();

  // ids permitidos para este usuario
  const allowedIds = user?.especialidades ?? [];

  const [data, setData] = useState<FormattedAsignatura[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchEspecialidades = async () => {
      setLoading(true);
      try {
        const resp = await get_especialidades();

        if (resp?.status === 200) {
          const raw = Array.isArray(resp.data?.data) ? resp.data.data : [];
          const especialidades: FormattedAsignatura[] = raw.map((e: any) => ({
            id: e.id_especialidad,
            nombre: e.name,
          }));
          setData(especialidades);
        } else {
          console.error("get_especialidades: respuesta inválida", resp);
          setData([]);
        }
      } catch (error) {
        console.error("Error consultando especialidades:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEspecialidades();
  }, []);

  const allowedSet = useMemo(() => new Set(allowedIds), [allowedIds]);

  const visibleData = useMemo(() => {
    //Si especialidades se encuentra vacío, se muestran todas las especialidades
    if (!allowedIds.length) return data; 
    return data.filter((esp) => allowedSet.has(esp.id));
  }, [data, allowedIds.length, allowedSet]);

  
  useEffect(() => {
    if (!selectedEspecialidadId) return;
    const stillValid = visibleData.some((x) => x.id === selectedEspecialidadId);
    if (!stillValid) {
      
      if (visibleData.length === 1) {
        onEspecialidadChange(visibleData[0]);
      } else {
       
      }
    }
  }, [selectedEspecialidadId, visibleData, onEspecialidadChange]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value;
    const seleccion = visibleData.find((item) => item.id === selectedId);
    if (seleccion) onEspecialidadChange(seleccion);
  };

  const renderValue = (value: string) => {
    if (!value) return "Selecciona por ejemplo: Programación";
    const found = visibleData.find((x) => x.id === value);
    return found?.nombre ?? "Especialidad";
  };

  const isDisabled = disabled || loading || visibleData.length === 0;

  return (
    <Grid item xs={12}>
      <Box>
        <Typography
          sx={{
            fontFamily: "montserrat",
            fontSize: "1.25rem",
            fontWeight: "bold",
            color: "#336B87",
          }}
        >
          Especialidad *
        </Typography>

        <FormControl fullWidth>
          <Select
            labelId="select-especialidad"
            id="select-especialidad"
            variant="outlined"
            value={selectedEspecialidadId || ""}
            displayEmpty
            onChange={handleChange}
            renderValue={renderValue}
            sx={{
              fontFamily: "montserrat",
              fontSize: "14px",
              borderRadius: 2,
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
              "& fieldset": { borderColor: "#BDBDBD" },
              "&:hover fieldset": { borderColor: "#9E9E9E" },
              "&.Mui-focused fieldset": { borderColor: "#005559" },
              "& .MuiSelect-select": {
                color: selectedEspecialidadId ? "#005559" : "#B3D1D1",
              },
            }}
          >
            <MenuItem value="" disabled>
              {loading ? "Cargando..." : "Selecciona por ejemplo: Programación"}
            </MenuItem>

            {loading && (
              <MenuItem value="" disabled>
                <Box display="flex" alignItems="center" gap={1}>
                 <SpinnerUmmIA logoWidth={20} logoHeight = {20}/>
                  Cargando...
                </Box>
              </MenuItem>
            )}

            {!loading && visibleData.length === 0 && (
              <MenuItem value="" disabled>
                No tienes especialidades asignadas
              </MenuItem>
            )}

            {visibleData.map((esp) => (
              <MenuItem key={esp.id} value={esp.id} sx={{ color: "#005559" }}>
                <Typography
                  sx={{
                    fontFamily: "montserrat",
                    fontSize: "0.8rem",
                    color: "#336B87",
                  }}
                >
                  {esp.nombre}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Grid>
  );
};

export default GetEspecialidad;
